import Point from 'esri/geometry/Point';
import SpatialReference from 'esri/SpatialReference';
import webMercatorUtils from 'esri/geometry/webMercatorUtils';

import { Promise as bbPromise } from 'promise';

import { criteriaStore } from 'stores/CriteriaStore';

import {
  formatWindValue,
  formatWindMosaicDatasetResults,
  getMriValuesFromQueriesResults,
  getMriValuesFromFormattedMosaicResults,
} from 'utils/windUtil';

import { windQuery, determineWindDebrisNote, determineWindDataSource, determineWindNote } from 'js/loads/wind';
import standardsConfig from 'js/standards.config';
import { query_pr_usvi_zone } from 'js/loads/other';

const queryWind = (location, load) => {
  return new bbPromise((resolve, reject) => {
    const { standardsVersion, riskLevel, measurement } = criteriaStore.getState();
    const windReoccurenceEndpoints =
      standardsVersion === '7-22'
        ? standardsConfig[standardsVersion].windServices.reoccurences
        : load.layers.queryDictionary.windReoccurence[standardsVersion];
    const geo = new Point(
      webMercatorUtils.lngLatToXY(location.long, location.lat),
      new SpatialReference({ wkid: 102100 })
    );

    try {
      query_pr_usvi_zone(geo, load).then((prUSVIResult) => {
        const isPRGeometry = prUSVIResult.features.length > 0;

        windQuery(geo, load, riskLevel, standardsVersion, isPRGeometry)
          .then((results) => {
            load.showInReport = true;

            const [
              hurricaneWindDebrisResult,
              hurricaneWindDebrisMileResult,
              windValueResult,
              specialZoneResult,
              swrMosaicDatasetResults,
              stateResult,
              ...recurrenceIntervalResults
            ] = results;

            const state = stateResult.features.length !== 0 ? stateResult.features[0].attributes.STATE_NAME : '';

            if (standardsVersion === '7-22') {
              load.isIslandTerritory = standardsConfig[standardsVersion].islandTerritories.includes(state);
            }

            if (load.isIslandTerritory && recurrenceIntervalResults.find((res) => res.value !== 'NoData')) {
              const windValue = recurrenceIntervalResults.find((res) => res.value !== 'NoData');
              const mph = windValue.value;
              const windSpeed = formatWindValue(windValue.value, measurement);

              load.sampleData.value = [windSpeed];

              load.details = [
                {
                  type: 'singleLine',
                  label: '10,000-year MRI',
                  value: windSpeed,
                },
              ];

              const isProneWindDebris = hurricaneWindDebrisResult.features.length ? true : false;
              const isProneWindDebrisOneMile = hurricaneWindDebrisMileResult.features.length ? true : false;

              if (specialZoneResult.features.length) {
                load.sampleData.value.push('Special Wind Region - See Details');
                specialZoneResult.features.forEach((feature) => {
                  const spec_wind = feature.attributes.spec_wind ? feature.attributes.spec_wind : 'Special Wind Region';
                  if (standardsVersion === '7-22') {
                    load.details.push({
                      type: 'note',
                      note:
                        spec_wind +
                        ' -- This site is in a special wind region as shown in Fig. 26.5-1 and should be examined for unusual wind conditions. The Authority Having Jurisdiction shall, if necessary, adjust the values given in Fig. 26.5-1 to account for higher local wind speeds. Such adjustment shall be based on meteorological information and an estimate of the basic wind speed obtained in accordance with the provisions in Section 26.5.3.',
                    });
                  } else {
                    load.details.push({
                      type: 'singleLine',
                      label: 'Special',
                      value:
                        spec_wind +
                        ' -- Mountainous terrain, gorges, and special wind regions shown in Fig. 26.5-1 shall be examined for unusual wind conditions. The Authority Having Jurisdiction shall, if necessary, adjust the values given in Fig. 26.5-1 to account for higher local wind speeds. Such adjustment shall be based on meteorological information and an estimate of the basic wind speed obtained in accordance with the provisions in Section 26.5.3.',
                    });
                  }
                });
              }

              load.details.push({
                type: 'note',
                note: determineWindNote(riskLevel, standardsVersion, measurement, state),
              });
              determineWindDebrisNote(
                riskLevel,
                standardsVersion,
                isProneWindDebris,
                isProneWindDebrisOneMile,
                parseFloat(mph),
                geo,
                load
              ).then((debrisNote) => {
                if (debrisNote && debrisNote.length) {
                  load.details.push({
                    type: 'note',
                    note: debrisNote,
                  });
                }
                load.details.push({
                  type: 'dataSource',
                  dataSource: {
                    hyperlinkUrl: '',
                    hyperlinkText: determineWindDataSource(standardsVersion, riskLevel, state),
                  },
                });
                resolve({ load: load.label });
              });
            } else {
              // no data
              if (windValueResult.value === 'NoData') {
                load.sampleData.value = ['No Information Available'];
                load.details = [
                  {
                    type: 'singleLine',
                    label: 'Wind Speed',
                    value: 'No Information Available',
                  },
                ];
                load.details.push({
                  type: 'note',
                  note: `Value provided is 3-second gust wind speeds at ${
                    measurement === 'si' ? '10 m' : '33 ft'
                  } above ground for Exposure C Category, based on linear interpolation between contours.`,
                });
                resolve({ load: load.label });
                // has data
              } else {
                const isProneWindDebris = hurricaneWindDebrisResult.features.length ? true : false;
                const isProneWindDebrisOneMile = hurricaneWindDebrisMileResult.features.length ? true : false;

                let occuranceEndpoints = { ...windReoccurenceEndpoints };

                if (state.toLowerCase() === 'hawaii') {
                  Object.keys(occuranceEndpoints).forEach((key) => {
                    if (Number(key) > 100) {
                      delete occuranceEndpoints[key];
                    }
                  });
                }

                let mph;
                let mriValues;

                let riskCategoryMRIDictionary;

                const swrMosaicDatasetsResultFound = swrMosaicDatasetResults
                  ? swrMosaicDatasetResults.find(
                      ({ results }) => results.length && results[0] && results[0].value && results[0].value !== 'NoData'
                    )
                  : undefined;

                // Set mph and mriValues based on found results
                if (swrMosaicDatasetsResultFound && swrMosaicDatasetsResultFound.results.length) {
                  riskCategoryMRIDictionary = standardsConfig[standardsVersion].windServices.riskCategoryMRIDictionary;
                  const formattedResults = formatWindMosaicDatasetResults(
                    swrMosaicDatasetsResultFound && swrMosaicDatasetsResultFound.results.length
                      ? swrMosaicDatasetsResultFound.results[0]
                      : undefined
                  );

                  mph = formattedResults[riskCategoryMRIDictionary[`riskCategory${riskLevel}`]];
                  mriValues = getMriValuesFromFormattedMosaicResults(formattedResults, measurement);
                } else if (standardsVersion === '7-22') {
                  riskCategoryMRIDictionary = standardsConfig[standardsVersion].windServices.riskCategoryMRIDictionary;
                  const formattedResults = formatWindMosaicDatasetResults(windValueResult);

                  mph = formattedResults[riskCategoryMRIDictionary[`riskCategory${riskLevel}`]];
                  mriValues = getMriValuesFromFormattedMosaicResults(formattedResults, measurement);
                } else {
                  mph = windValueResult.value;
                  mriValues = getMriValuesFromQueriesResults(
                    occuranceEndpoints,
                    recurrenceIntervalResults,
                    measurement
                  );
                }

                const windSpeed = !isNaN(mph) ? formatWindValue(mph, measurement) : mph;

                load.sampleData.value = [windSpeed];

                load.details = [
                  {
                    type: 'singleLine',
                    label: 'Wind Speed',
                    value: windSpeed,
                  },
                  ...mriValues,
                ];

                if (specialZoneResult.features.length) {
                  load.sampleData.value.push('Special Wind Region - See Details');
                  specialZoneResult.features.forEach((feature) => {
                    const spec_wind = feature.attributes.spec_wind
                      ? feature.attributes.spec_wind
                      : 'Special Wind Region';
                    if (standardsVersion === '7-22') {
                      load.details.push({
                        type: 'note',
                        note:
                          spec_wind +
                          ' -- This site is in a special wind region as shown in Fig. 26.5-1 and should be examined for unusual wind conditions. The Authority Having Jurisdiction shall, if necessary, adjust the values given in Fig. 26.5-1 to account for higher local wind speeds. Such adjustment shall be based on meteorological information and an estimate of the basic wind speed obtained in accordance with the provisions in Section 26.5.3.',
                      });
                    } else {
                      load.details.push({
                        type: 'singleLine',
                        label: 'Special',
                        value:
                          spec_wind +
                          ' -- Mountainous terrain, gorges, and special wind regions shown in Fig. 26.5-1 shall be examined for unusual wind conditions. The Authority Having Jurisdiction shall, if necessary, adjust the values given in Fig. 26.5-1 to account for higher local wind speeds. Such adjustment shall be based on meteorological information and an estimate of the basic wind speed obtained in accordance with the provisions in Section 26.5.3.',
                      });
                    }
                  });
                }
                load.details.push({
                  type: 'note',
                  note: determineWindNote(riskLevel, standardsVersion, measurement, state),
                });
                determineWindDebrisNote(
                  riskLevel,
                  standardsVersion,
                  isProneWindDebris,
                  isProneWindDebrisOneMile,
                  parseFloat(mph),
                  geo,
                  load
                ).then((debrisNote) => {
                  if (debrisNote && debrisNote.length) {
                    load.details.push({
                      type: 'note',
                      note: debrisNote,
                    });
                  }
                  load.details.push({
                    type: 'dataSource',
                    dataSource: {
                      hyperlinkUrl: '',
                      hyperlinkText: determineWindDataSource(standardsVersion, riskLevel, state),
                    },
                  });
                  resolve({ load: load.label });
                });
              }
            }
          })
          .catch((err) => {
            console.log(err);
            reject({ load: load.label, error: err });
          });
      });
    } catch (error) {
      console.log(error);
      reject({ load: load.label, error });
    }
  });
};

export default queryWind;
